import { Page } from 'braid-design-system';
import queryString from 'query-string';
import { Fragment, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Redirect from 'src/components/Redirect/Redirect';
import { SiteWideFooter } from 'src/components/SiteWideFooter';
import { getConfigValues } from 'src/config';
import Routes from 'src/config/routes';
import { useAuth } from 'src/hooks/auth';

import LegacyAuth0Authenticated from './legacy/Auth0Authenticated';
import LegacyAuthenticated from './legacy/Authenticated';

type AuthMode =
  | 'simple'
  | 'legacy-authenticated'
  | 'legacy-auth0-authenticated';

interface AuthenticatedProps {
  children: ReactNode;
  /**
   * Authentication mode:
   * - 'simple': Basic auth check (x-token or auth token)
   * - 'legacy-authenticated': Use original Authenticated component (full account verification)
   * - 'legacy-auth0-authenticated': Use original Auth0Authenticated component (Auth0 token only)
   */
  mode: AuthMode;
}

const Authenticated = ({ children, mode }: AuthenticatedProps) => {
  // Legacy modes: use exact copies of original components
  if (mode === 'legacy-authenticated') {
    return <LegacyAuthenticated>{children}</LegacyAuthenticated>;
  }

  if (mode === 'legacy-auth0-authenticated') {
    return <LegacyAuth0Authenticated>{children}</LegacyAuth0Authenticated>;
  }

  // Simple mode: check if either x-token or auth token exists
  return <SimpleAuthenticated>{children}</SimpleAuthenticated>;
};

export default Authenticated;

// Simple auth guard component
const SimpleAuthenticated = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { getAuthToken, getXToken, authLoaded } = useAuth();

  // Wait for auth to load
  if (!authLoaded) {
    return (
      <Page footer={<SiteWideFooter />} footerPosition="belowFold">
        <CustomLoader />
      </Page>
    );
  }

  const authToken = getAuthToken();
  const xToken = getXToken();

  // Basic token format validation to avoid polluting backend error metrics
  // Tokens should be non-empty and have minimum length
  const isValidTokenFormat = (token: string | null | undefined) =>
    token && token.length > 10;

  const hasValidAuth =
    isValidTokenFormat(authToken) || isValidTokenFormat(xToken);

  // If authenticated, render children
  if (hasValidAuth) {
    return <Fragment>{children}</Fragment>;
  }

  // Not authenticated - redirect to sign in
  const redirectUrl = queryString.stringifyUrl({
    url: `${getConfigValues().HEADER_SERVER_BASE}${Routes.SIGN_IN_URL}`,
    query: {
      ReturnUrl: location.pathname + location.search,
    },
  });

  return <Redirect path={redirectUrl} />;
};
